import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  GetVariationsProductDetailsGridResponse,
  VariationsDetailGridMetricType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { BeaconProTable } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProTable/BeaconProTable';
import { GRID_ITEMS_PER_PAGE } from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useMainEntityType, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import NoDataPlaceHolder, {
  NoDataIcon
} from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { NewBeaconFileIcon } from 'src/components/SvgIcons';
import { useBeaconFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBeaconFilters';
import { GetEntityMetadataResponseRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import { VariationsProductDetailGridAdapter } from './adapters/VariationsProductDetailGridAdapter';
import { Product } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { parentColumnDefinitions, childColumnDefinitions } from './VariationsProductDetailsGridColumnDefinitions';
import { FIVE_MIN, THIRTY_MIN, RESULTS_COUNT_THRESHOLD } from 'src/utils/constants';
import ProductExportModal from './ProductExportModal';
import { ContentSubTabs } from './utils';
import { useVariationsFilterStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/VariationsFilterStore';
import {
  FilterLink,
  useFilterRendering
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useFilterRendering';
import VariationsFilterModal from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Modals/VariationsFilterModal';
import colors from 'src/utils/colors';

interface VariationsProductDetailsResponse {
  main: GetVariationsProductDetailsGridResponse;
  comparison: GetVariationsProductDetailsGridResponse;
  productsMap: Record<string, Product>;
}

interface VariationsProductDetailsGridCardsProps {
  title: string;
  queryFn: (
    requestType: VariationsDetailGridMetricType,
    variationsFilterType?: string,
    offset?: number
  ) => Promise<VariationsProductDetailsResponse>;
  renderSubtitle?: (dropdownOption: VariationsDetailGridMetricType) => string;
}

const dropdownOptionToDisplayName = {
  [VariationsDetailGridMetricType.PageDetailGrid]: 'Parent SKU',
  [VariationsDetailGridMetricType.ChildDetailGrid]: 'Child SKU'
};

const formatMetricName = (name: string) => {
  return name
    .toLowerCase()
    .replace(/\bskus\b/gi, 'SKUs')
    .replace(/\bsku\b/gi, 'SKU');
};

const VariationsProductDetailsGridCards: React.FC<VariationsProductDetailsGridCardsProps> = ({
  queryFn,
  renderSubtitle
}) => {
  const [page, setPage] = useState(1);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const entityType = useMainEntityType();
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const { appFilters } = useBeaconFilters();

  const {
    openModal,
    getActiveFilter,
    dropdownOption,
    setDropdownOption,
    isSingleSkuPagesEnabled,
    isVariatedSkuPagesEnabled,
    isCorrectChildSkusEnabled,
    isIncorrectChildSkusEnabled,
    isInaccurateParentPagesEnabled
  } = useVariationsFilterStore();
  const activeFilters = useMemo(
    () => getActiveFilter(),
    [
      isSingleSkuPagesEnabled,
      isVariatedSkuPagesEnabled,
      isCorrectChildSkusEnabled,
      isIncorrectChildSkusEnabled,
      isInaccurateParentPagesEnabled
    ]
  );
  const { renderFilters } = useFilterRendering(activeFilters);

  const variationsFilterType = useMemo(() => {
    const filters = [
      { condition: isSingleSkuPagesEnabled, value: 'singleSkuPages' as const },
      { condition: isVariatedSkuPagesEnabled, value: 'variatedSkuPages' as const },
      { condition: isCorrectChildSkusEnabled, value: 'correctChildSkus' as const },
      { condition: isIncorrectChildSkusEnabled, value: 'incorrectChildSkus' as const },
      { condition: isInaccurateParentPagesEnabled, value: 'inaccurateParentPages' as const }
    ];

    return filters.find((filter) => filter.condition)?.value;
  }, [
    isSingleSkuPagesEnabled,
    isVariatedSkuPagesEnabled,
    isCorrectChildSkusEnabled,
    isIncorrectChildSkusEnabled,
    isInaccurateParentPagesEnabled
  ]);

  const filtersKey = useMemo(() => {
    return appFilters ? JSON.stringify(appFilters) : '';
  }, [appFilters]);

  const variationsTableFiltersKey = useMemo(() => {
    return activeFilters.length > 0 ? JSON.stringify(activeFilters) : '';
  }, [activeFilters]);

  // Primary data query
  const {
    data: variationsQueryData,
    isLoading,
    isFetchingNextPage: isLoadingMore,
    hasNextPage: hasMoreData,
    fetchNextPage
  } = useInfiniteQuery<VariationsProductDetailsResponse>({
    queryKey: [
      'variations-product-details',
      entityType,
      dropdownOption,
      mainTimePeriod.startWeek,
      mainTimePeriod.endWeek,
      comparisonTimePeriod.startWeek,
      comparisonTimePeriod.endWeek,
      filtersKey,
      variationsTableFiltersKey,
      variationsFilterType
    ],
    queryFn: (context) => queryFn(dropdownOption, variationsFilterType, (context.pageParam as number) || 0),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalRecords = allPages.reduce((sum, page) => sum + page.main.data.records.length, 0);
      return totalRecords >= RESULTS_COUNT_THRESHOLD ? totalRecords : undefined;
    },
    staleTime: FIVE_MIN,
    gcTime: THIRTY_MIN
  });

  // Combine all product metadata from all pages
  const productMetadataMap = useMemo(() => {
    if (!variationsQueryData?.pages) return new Map<string, GetEntityMetadataResponseRecord>();

    const combinedMap = new Map<string, GetEntityMetadataResponseRecord>();
    variationsQueryData.pages.forEach((page) => {
      Object.entries(page.productsMap).forEach(([sku, product]) => {
        combinedMap.set(sku, product as GetEntityMetadataResponseRecord);
      });
    });

    return combinedMap;
  }, [variationsQueryData]);

  // Process all loaded data from infinite query
  const allLoadedData = useMemo(() => {
    if (!variationsQueryData?.pages) return [];

    const allMainRecords = variationsQueryData.pages.flatMap((page) => page.main.data.records);
    const allComparisonRecords = variationsQueryData.pages.flatMap((page) => page.comparison.data.records);

    const adapter = new VariationsProductDetailGridAdapter(
      allMainRecords,
      allComparisonRecords,
      dropdownOption,
      productMetadataMap
    );

    return adapter.transform();
  }, [variationsQueryData, productMetadataMap, dropdownOption]);

  useEffect(() => {
    setPage(1);
  }, [
    dropdownOption,
    mainTimePeriod.startWeek,
    mainTimePeriod.endWeek,
    comparisonTimePeriod.startWeek,
    comparisonTimePeriod.endWeek,
    filtersKey,
    variationsTableFiltersKey
  ]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const currentColumnDefinitions =
    dropdownOption === VariationsDetailGridMetricType.PageDetailGrid ? parentColumnDefinitions : childColumnDefinitions;
  const totalPages = Math.ceil(allLoadedData.length / GRID_ITEMS_PER_PAGE);
  const showPagination = totalPages > 1 || hasMoreData;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text variant="h4">Page Detail by</Text>
          <SlDropdownMenu
            onChange={(selectedOption) => setDropdownOption(selectedOption.id as VariationsDetailGridMetricType)}
            defaultLabel={dropdownOptionToDisplayName[dropdownOption]}
            selectedId={dropdownOption}
            buttonVariant="title"
            options={Object.values(VariationsDetailGridMetricType).map((option) => ({
              id: option,
              label: dropdownOptionToDisplayName[option]
            }))}
          />
        </div>
        <Flex gap={16}>
          <SlButton active={activeFilters.length > 0} onClick={openModal}>
            Filter
          </SlButton>
          <SlButton
            onClick={() => {
              setIsExportModalOpen(true);
            }}
          >
            Export
          </SlButton>
        </Flex>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Text variant="body1" sx={{ marginTop: '8px' }}>
          {activeFilters.length > 0 ? (
            <>
              The table displays page details by {formatMetricName(dropdownOptionToDisplayName[dropdownOption])} with{' '}
              <span>{renderFilters(activeFilters)}</span> filter applied.{' '}
              <FilterLink clickable onClick={openModal}>
                Edit Filter
              </FilterLink>
              .
            </>
          ) : (
            renderSubtitle(dropdownOption)
          )}
        </Text>
      </div>

      <Flex sx={{ marginTop: '32px' }} flexDirection="column" alignItems="flex-start">
        {!isLoading && allLoadedData.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              width: '100%',
              padding: '40px'
            }}
          >
            <NoDataPlaceHolder
              title="Data currently unavailable"
              description="There is currently no data matching your selected criteria. Please try another query to view insights."
              icon={<NoDataIcon icon={<NewBeaconFileIcon />} />}
            />
          </div>
        ) : (
          <>
            <BeaconProTable isLoading={isLoading} data={allLoadedData} page={page} columns={currentColumnDefinitions} />

            {showPagination && (
              <div
                style={{ marginTop: '24px', alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: '16px' }}
              >
                {isLoadingMore && (
                  <Text variant="body2" color="secondary">
                    Loading more data...
                  </Text>
                )}
                <Pagination
                  currentPage={page}
                  totalPages={Math.max(totalPages, page + (hasMoreData ? 1 : 0))}
                  onClickNext={() => {
                    const nextPage = page + 1;
                    const totalLoadedItems = allLoadedData.length;
                    const itemsNeeded = nextPage * GRID_ITEMS_PER_PAGE;

                    if (itemsNeeded > totalLoadedItems && hasMoreData && !isLoadingMore) {
                      fetchNextPage().then(() => handlePageChange(nextPage));
                    } else {
                      handlePageChange(nextPage);
                    }
                  }}
                  onClickPrevious={() => handlePageChange(Math.max(page - 1, 1))}
                />
              </div>
            )}
          </>
        )}
      </Flex>
      <ProductExportModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        exportType={
          dropdownOption === VariationsDetailGridMetricType.PageDetailGrid
            ? ContentSubTabs.contentVariationsParent
            : ContentSubTabs.contentVariationsChild
        }
      />
      <VariationsFilterModal dropdownOption={dropdownOption} />
    </div>
  );
};

export default VariationsProductDetailsGridCards;
